import * as astrolib from "./js/astrolib.js";
import { SphereGeometry } from "./SphereGeometry";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";

export class Orbit3dDisplay {

    time: number;
    scene: THREE.Scene;
    container: HTMLDivElement;
    renderer: THREE.WebGLRenderer;
    camera: THREE.OrthographicCamera;
    earth: THREE.Mesh;
    satellites: THREE.Group;
    sunlitOrbitMaterial: THREE.LineBasicMaterial;
    eclipsedOrbitMaterial: THREE.LineBasicMaterial;
    sunlitPrimarySatMaterial: THREE.PointsMaterial;
    eclipsedPrimarySatMaterial: THREE.PointsMaterial;
    sunlitSatMaterial: THREE.PointsMaterial;
    eclipsedSatMaterial: THREE.PointsMaterial;
    earthGroup: THREE.Group;
    trackball: OrbitControls;
    selectedSat: THREE.Object3D;
    primarySat: THREE.Points;

    private _2dCanvas: HTMLCanvasElement;
    private _2dContext: CanvasRenderingContext2D;
    private _showGrid: boolean = true;
    private _mouseX: number = 1;
    private _mouseY: number;
    private _mouseDownX: number;
    private _mouseDownY: number;
    private _stations: THREE.Object3D[];
    private _primaryOrbit: THREE.Group;
    private _satGeom: THREE.BufferGeometry;
    private _uniforms: any;
    private fragment_shader = `
        uniform sampler2D texture;
        uniform vec3 sunPos;
        varying vec2 vUv;

        void main( void ) {
            vec2 position = -1.0 + 2.0 * vUv;
            float lat = radians(position.y * 90.0);
            float lng = radians(position.x * 180.0);
            vec3 pos = vec3(cos(lat) * cos(lng), cos(lat) * sin(lng), sin(lat));
            vec3 color = texture2D( texture, vUv ).rgb;
            float dotprod = dot(sunPos, pos);
            if (dotprod < 0.0)
            {
                float fac = 0.5;
                gl_FragColor = vec4(color.x * fac, color.y * fac, color.z * fac, 1.0 );
            }
        else
            gl_FragColor = vec4( color.x, color.y, color.z, 1.0 );
        }
    `;
    private vertex_shader = `
        varying vec2 vUv;
        void main()
        {
            vUv = uv;
            vec4 mvPosition = modelViewMatrix * vec4( position, 1.0 );
            gl_Position = projectionMatrix * mvPosition;
        }
    `;

    constructor(def: Orbit3dDisplayDef) {
        this.time = def.time;
        this.container = def.container;

        // create the 3D scene
        this.scene = new THREE.Scene();

        // earth radius in km
        let erad = astrolib.Earth.RADIUS;

        // create the camera
        let boxWidth = 8000;
        this.camera = new THREE.OrthographicCamera(-boxWidth, boxWidth, boxWidth, -boxWidth, 100, 20000);
        this.camera.position.set(-8000, 0, 0);
        this.camera.lookAt(new THREE.Vector3(0, 0, 0));
        this.camera.up.set(0, 0, 1);
        this.camera.updateProjectionMatrix();

        this.renderer = new THREE.WebGLRenderer({ antialias: true });
        this.renderer.setClearColor(0x000000, 1);

        let w = this.container.clientWidth;
        let h = this.container.clientHeight;
        this.renderer.setSize(w, h);
        this.container.appendChild(this.renderer.domElement);

        // create 2D overlay for text labels
        this._2dCanvas = document.createElement("canvas");
        this.container.appendChild(this._2dCanvas);
        this._2dContext = this._2dCanvas.getContext("2d");
        this._2dCanvas.width = w;
        this._2dCanvas.height = h;
        this._2dCanvas.style.top = "0";
        this._2dCanvas.style.left = "0";
        this._2dCanvas.style.width = w + "px";
        this._2dCanvas.style.height = h + "px";
        this._2dCanvas.style.position = 'absolute';
        this._2dCanvas.style.zIndex = '100';
        this._2dCanvas.style.padding = '0';
        this._2dCanvas.style.margin = '0';
        this._2dCanvas.style.pointerEvents = 'none';

        // the satellites are downloaded separately and stored in a Group
        this.satellites = new THREE.Group();
        this.scene.add(this.satellites);

        // trackball controls
        this.trackball = new OrbitControls(this.camera, this.container);

        // materials used for drawing orbital paths
        this.sunlitOrbitMaterial = new THREE.LineBasicMaterial({ color: 0xffffff, linewidth: 1 });
        this.eclipsedOrbitMaterial = new THREE.LineBasicMaterial({ color: 0x808080, linewidth: 1 });

        let texture = new THREE.TextureLoader().load(def.earthTextureUrl);

        this._uniforms = {
            texture: { value: texture }
        };

        var earthMaterial = new THREE.ShaderMaterial({
            uniforms: this._uniforms,
            vertexShader: this.vertex_shader,
            fragmentShader: this.fragment_shader
        });

        this.earth = new THREE.Mesh(new SphereGeometry(erad, 5, 5), earthMaterial);

        this.earthGroup = new THREE.Group();
        this.earthGroup.add(this.earth);
        this.scene.add(this.earthGroup);

        // latitude/longitude grid
        let geometry = new THREE.Geometry();
        let gridMaterial = new THREE.LineBasicMaterial({ color: 0x008080, linewidth: 1 })

        // lines of longitude
        for (let lat = -80; lat <= 80; lat += 2) {
            let radLat = lat * astrolib.DEGTORAD;
            let pos = new THREE.Vector3(Math.cos(radLat), 0, Math.sin(radLat)).multiplyScalar(astrolib.Earth.RADIUS + 1);
            geometry.vertices.push(pos);
        }
        for (let lng = 0; lng < 360; lng += 30) {
            let lineOfLongitude = new THREE.Line(geometry, gridMaterial);
            lineOfLongitude.rotation.set(0, 0, lng * astrolib.DEGTORAD);
            this.earthGroup.add(lineOfLongitude);
        }

        // lines of latitude
        let latitudes = [-80, -60, -30, 0, 30, 60, 80];
        for (let i = 0; i < latitudes.length; i++) {
            let lat = latitudes[i];
            let geometry = new THREE.Geometry();
            let z = Math.sin(lat * astrolib.DEGTORAD);
            let xy = Math.cos(lat * astrolib.DEGTORAD);
            for (let lng = 0; lng <= 360; lng += 2) {
                let radLng = lng * astrolib.DEGTORAD;
                let pos = new THREE.Vector3(xy * Math.cos(radLng), xy * Math.sin(radLng), z).multiplyScalar(astrolib.Earth.RADIUS + 1);
                geometry.vertices.push(pos);
            }
            let lineOfLatitude = new THREE.Line(geometry, gridMaterial);
            this.earthGroup.add(lineOfLatitude);
        }

        // ground stations
        this._stations = [];
        for (let gs = 0; gs < def.groundStations.length; gs++) {
            let station = new THREE.Object3D();
            station.userData = def.groundStations[gs];
            let latrad = station.userData.latitude * astrolib.DEGTORAD;
            let lngrad = station.userData.longitude * astrolib.DEGTORAD;
            let x = Math.cos(latrad) * Math.cos(lngrad);
            let y = Math.cos(latrad) * Math.sin(lngrad);
            let z = Math.sin(latrad);
            station.position.set(x, y, z);
            station.position.multiplyScalar(astrolib.Earth.RADIUS);
            this.earthGroup.add(station);
            this._stations.push(station);
        }

        // satellite geometry and materials
        this._satGeom = new THREE.BufferGeometry();
        let positions = [];
        positions.push(0, 0, 0);
        this._satGeom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        this._satGeom.computeBoundingSphere();
        this.sunlitPrimarySatMaterial = new THREE.PointsMaterial({ size: def.satSize, color: def.primarySatColor, sizeAttenuation: false });
        this.eclipsedPrimarySatMaterial = new THREE.PointsMaterial({ size: def.satSize, color: def.primarySatEclipseColor, sizeAttenuation: false });
        this.sunlitSatMaterial = new THREE.PointsMaterial({ size: def.satSize, color: def.secondarySatColor, sizeAttenuation: false });
        this.eclipsedSatMaterial = new THREE.PointsMaterial({ size: def.satSize, color: def.secondarySatEclipseColor, sizeAttenuation: false });

        this._2dContext.font = "14px Arial";

        this.time = astrolib.Time.utcNow;

        if (def.initialObserverLat !== undefined)
            this.setViewLatLong(def.initialObserverLat, def.initialObserverLong);

        this.onGotData();
        this.render();
        this.animate();

    }

    /** called when the data file has been loaded to set up the scene objects */
    private onGotData() {

        // create orbit of primary satellite as a LINE object
        let orbitMat = new THREE.LineBasicMaterial({ color: 0xffffff, linewidth: 1 })

        // primary orbit
        this._primaryOrbit = new THREE.Group();
        this.updatePrimaryOrbit(this.time);

        this.scene.add(this._primaryOrbit);

        let self = this;
        this.container.addEventListener('mousemove', function (ev) {
            self._mouseX = ev.offsetX;
            self._mouseY = ev.offsetY;
        }, false);

        this.container.addEventListener('mousedown', function (ev) {
            self._mouseDownX = ev.offsetX;
            self._mouseDownY = ev.offsetY;
        }, false);

        this.container.addEventListener('click', function (ev) {
            // we only change the satellite selection if the mouse has moved significantly
            let dx = ev.offsetX - self._mouseDownX;
            let dy = ev.offsetY - self._mouseDownY;
            let dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < 10) {
                if (self.selectedSat != null) {
                    self.setPrimarySatellite(self.selectedSat.userData.id);
                    //alert('you clicked ' + self.selectedSat.userData.id);
                }
                else
                    self.setPrimarySatellite(NaN);
            }
        }, false);
    }

    /** Updates the primary orbit for the given time */
    private updatePrimaryOrbit(time: number) {
        this._primaryOrbit.children = [];
        if (!this.primarySat)
            return;
        let orbit = this.primarySat.userData.orbit;
        let period = orbit.period;
        let dt = period / 100;
        let started = false;
        let inSunlight: boolean;
        let geometry: THREE.Geometry;

        for (let t = time - period / 2; t <= time + period / 2 + dt; t += dt) {
            if (!started) {
                // start a new geometry
                geometry = new THREE.Geometry();
                inSunlight = orbit.isSunlit(t);
                this._primaryOrbit.add(new THREE.Line(geometry, inSunlight ? this.sunlitOrbitMaterial : this.eclipsedOrbitMaterial))
            }
            let sv = orbit.getPos(t);
            let pos = new THREE.Vector3(sv.x, sv.y, sv.z);

            // has there been a change in lighting?
            if (inSunlight != orbit.isSunlit(t)) {
                // find exact time of change
                let tChange = orbit.findNextSunRiseSet(t - dt, dt * 1.5);
                sv = orbit.getPos(tChange);
                pos = new THREE.Vector3(sv.x, sv.y, sv.z);
                geometry.vertices.push(pos);
                t = tChange - dt;
                started = false;
            }
            else {
                geometry.vertices.push(pos);
                started = true;
            }
        }

        // store the time for which the orbit is valid
        this._primaryOrbit.userData.time = this.time;
    }

    toScreenPosition(obj: THREE.Object3D) {
        var vector = new THREE.Vector3();

        var widthHalf = 0.5 * this.renderer.getContext().canvas.width;
        var heightHalf = 0.5 * this.renderer.getContext().canvas.height;

        obj.updateMatrixWorld(true);
        this.camera.updateMatrixWorld(true);
        vector.setFromMatrixPosition(obj.matrixWorld);
        let z = vector.z;
        vector.project(this.camera);

        vector.x = (vector.x * widthHalf) + widthHalf;
        vector.y = - (vector.y * heightHalf) + heightHalf;

        return {
            x: vector.x,
            y: vector.y,
            z: vector.z
        };
    };

    /** Creates a new THREE.Vector from an HA Vector */
    haTo3Vector(vec: astrolib.Vector): THREE.Vector3 {
        return new THREE.Vector3(vec.x, vec.y, vec.z);
    }

    animate(): void {
        var self = this;

        // limit maximum frame rate to 30fps because more is unnecessary
        //setTimeout(function () {
        requestAnimationFrame(function () { self.animate() });
        //}, 1000 / 30);

        this.render();
        //++frames;
    }

    // resetView(): void {
    //     let satPos = this.haTo3Vector(this.orbits[0].getPos(this.time));

    //     // keep camera above primary satellite
    //     satPos.multiplyScalar(1.2);
    //     this.camera.position.copy(satPos);
    //     this.camera.lookAt(new THREE.Vector3(0, 0, 0));
    //     this.camera.updateProjectionMatrix();
    // }

    /** Set the observation poit to be above the given coordinates, with north upwards */
    setViewLatLong(lat: number, long: number): void {
        let z = Math.sin(lat * astrolib.DEGTORAD);
        let xy = Math.cos(lat * astrolib.DEGTORAD);
        let ra = long * astrolib.DEGTORAD + astrolib.Earth.gha(this.time);
        let x = xy * Math.cos(ra);
        let y = xy * Math.sin(ra);

        let cameraPos = new THREE.Vector3(x, y, z).multiplyScalar(1.2 * astrolib.Earth.RADIUS);
        //cameraPos.setFromSphericalCoords(1.2 * astrolib.Earth.RADIUS, long * astrolib.DEGTORAD - astrolib.Earth.gha(this.time), (90 - lat) * astrolib.DEGTORAD);

        // keep camera above primary satellite
        this.camera.position.copy(cameraPos);
        this.camera.lookAt(new THREE.Vector3(0, 0, 0));
        this.camera.updateProjectionMatrix();
    }

    render(): void {

        // clear the 2D canvas
        let w = this._2dCanvas.width;
        let h = this._2dCanvas.height;
        this._2dContext.clearRect(0, 0, w, h);

        let sunPos = astrolib.Sun.getRoughPos(this.time);
        let gha = astrolib.Earth.gha(this.time);

        this._uniforms.sunPos = { value: this.haTo3Vector(sunPos.rotateAboutZAxis(-gha)) }


        // do we need to update the primary orbit?
        if (Math.abs(this.time - this._primaryOrbit.userData.time) > 600000) {
            this.updatePrimaryOrbit(this.time);
        }

        // Earth rotation
        let q = new THREE.Quaternion();
        q.setFromAxisAngle(new THREE.Vector3(0, 0, 1), gha);
        this.earthGroup.setRotationFromQuaternion(q);

        // satellites
        for (let s = 0; s < this.satellites.children.length; s++) {
            let sat3d = <THREE.Points>this.satellites.children[s];
            let orbit = sat3d.userData.orbit;
            // we have to use a try/catch block here, because the orbit.getPos can throw an exception. This is probably if the 
            // orbit is propogated beyond expected decay
            try {
                if (orbit) {
                    let pos = orbit.getPos(this.time);
                    sat3d.position.copy(this.haTo3Vector(pos));
                }
                // set material according to lighting and if the satellite is selected
                if (sat3d === this.primarySat)
                    sat3d.material = (orbit.isSunlit(this.time) ? this.sunlitPrimarySatMaterial : this.eclipsedPrimarySatMaterial);
                else
                    sat3d.material = (orbit.isSunlit(this.time) ? this.sunlitSatMaterial : this.eclipsedSatMaterial);
            }
            catch { }
        }

        // keep camera above primary satellite
        //satPos.multiplyScalar(1.2);
        // this.camera.position.copy(satPos);
        // this.camera.lookAt(new THREE.Vector3(0,0,0));
        // this.camera.updateProjectionMatrix();
        this.trackball.update();

        // satellite label
        //this.renderLabel(this.primarySat, 'Sat', 'white', 'white');

        // station labels, if station is on "near-side" of Earth
        for (let gs = 0; gs < this._stations.length; gs++) {
            let station = this._stations[gs];
            let vector = new THREE.Vector3();
            vector.setFromMatrixPosition(station.matrixWorld);
            let dotProd = vector.dot(this.camera.position);
            if (dotProd > 0) {
                this.renderLabel(station, station.userData.name, 'white', 'white');
            }
        }

        // show popup if mouse cursor is near a satelite
        let minDelta = 1e300;
        let closestSat: THREE.Object3D = null;
        for (let s = 0; s < this.satellites.children.length; s++) {
            let sat3D = this.satellites.children[s];
            let orbit = sat3D.userData.orbit;
            let pnt = this.toScreenPosition(sat3D);
            if (pnt.z > 0)
                continue;
            let dx = pnt.x - this._mouseX;
            let dy = pnt.y - this._mouseY;
            let delta = Math.sqrt(dx * dx + dy * dy);
            if (delta < minDelta) {
                minDelta = delta;
                closestSat = sat3D;
            }
        }
        if (minDelta < 20) {
            this.selectedSat = closestSat;
            this.renderLabel(closestSat, closestSat.userData.name + '(' + closestSat.userData.id + ')', 'white', 'white');
            // this._2dCanvas = document.createElement("canvas");
            // this.container.appendChild(this._2dCanvas);

            // $(this.container).append('<div id="satPop" class="messagepop" />');
            // $('#satPop').append('Satellite: ' + s);
        }
        else
            this.selectedSat = null;

        this.renderer.render(this.scene, this.camera);
    }

    /** render a label associated with the given 3D object onto the 2D canvas overlay */
    renderLabel(obj: THREE.Object3D, text: string, fillStyle: string, strokeStyle: string) {
        let pnt = this.toScreenPosition(obj);
        this._2dContext.fillStyle = fillStyle;
        this._2dContext.fillText(text, pnt.x + 10, pnt.y + 10);
        this._2dContext.strokeStyle = strokeStyle;
        this._2dContext.beginPath();
        this._2dContext.arc(pnt.x, pnt.y, 4, 0, astrolib.TWOPI);
        this._2dContext.stroke();
    }

    /** render a descriptive message to the user */
    renderMessage(text: string) {
        if (!text)
            return;


        let x = this._2dCanvas.width / 2;
        let y = 30;
        this._2dContext.save();
        this._2dContext.font = '24px Arial';
        this._2dContext.fillStyle = 'white';
        this._2dContext.textAlign = 'center';
        this._2dContext.fillText(text, x, y);
        this._2dContext.restore();
    }

    setSatellites(satellites: Satellite[]) {
        this.satellites.children = [];

        // a satellite is modelled as a single point
        for (let s = 0; s < satellites.length; s++) {
            let sat = new THREE.Points(this._satGeom, this.sunlitSatMaterial);
            // if (s == 0)
            //     this.primarySat = sat;
            satellites[s].orbit = new astrolib.Sgp4Orbit(satellites[s].tle)
            sat.userData = satellites[s];
            this.satellites.add(sat);
        }
    }

    setPrimarySatellite(satid: number) {
        if (isNaN(satid)) {
            this.primarySat = null;
        }
        else {
            for (let s = 0; s < this.satellites.children.length; ++s) {
                if (this.satellites.children[s].userData.id == satid) {
                    this.primarySat = <THREE.Points>this.satellites.children[s];
                }
            }
        }
        this.updatePrimaryOrbit(this.time);
    }
}

export class Satellite {
    tle: string;
    name: string;
    id: number;
    cospar: string;
    orbit: any;
}

export class Orbit3dDisplayDef {
    time: number;
    container: HTMLDivElement;
    tles: string[];
    earthTextureUrl: string;
    satSize: number;
    primarySatColor: number;
    primarySatEclipseColor: number;
    groundStations: any[];
    secondarySatColor: number;
    secondarySatEclipseColor: number;
    initialObserverLat: number;
    initialObserverLong: number;
}
